import { useState } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import PageWithTitle from '../../components/atom/PageWithTitle';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { TABLE_ROW_COUNT_DEFAULT, TABLE_ROW_COUNT_OPTIONS } from '../../helpers/constants';
import {
    useGetVenueCommissionGroups,
    useGetCommissionGroupBookings,
} from '@openapi/commission-groups/commission-groups';
import { useGetShows } from '@openapi/shows/shows';
import { useIsCommissionGroupManager } from '../../hooks/useIsCommissionGroupManager';
import useNotify from '../../hooks/useNotify';
import SkeletonTable from '../../components/atom/SkeletonTable';
import { formatCentsToUsd } from '../../helpers/formatCentsToUsd';
import CommissionGroupMembers from '../commissiongroups/components/CommissionGroupMembers';
import AgentPerformanceTab from './AgentPerformanceTab';

const BOOKING_COLUMNS = [
    { field: 'agentName', tKey: 'agent' },
    { field: 'bookingId', tKey: 'booking' },
    { field: 'contact', tKey: 'contact' },
    { field: 'availability', tKey: 'availability' },
    { field: 'customers', tKey: 'customers' },
    { field: 'bookingStatus', tKey: 'status' },
    { field: 'lineTotal', tKey: 'lineTotal', align: 'right' },
    { field: 'commissionAmount', tKey: 'commission', align: 'right' },
];

const STATUS_COLOR = { finished: 'success', cancelled: 'error' };

const CommissionGroupManagement = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { notifyError } = useNotify();
    const navigate = useNavigate();

    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [sortField, setSortField] = useState('bookingId');
    const [sortDir, setSortDir] = useState('asc');
    const [bookingsPage, setBookingsPage] = useState(0);
    const [bookingsRowsPerPage, setBookingsRowsPerPage] = useState(TABLE_ROW_COUNT_DEFAULT);

    const {
        isSuperadmin,
        managerAssignments: ownManagerAssignments,
        isLoading: ownGroupsLoading,
    } = useIsCommissionGroupManager();

    const { data: venueGroupsData, isLoading: venueGroupsLoading } = useGetVenueCommissionGroups(
        Number(user?.venueId),
        {
            query: { enabled: !!user?.venueId && isSuperadmin, onError: notifyError },
        },
    );

    const managerAssignments = isSuperadmin
        ? (venueGroupsData?.commissionGroups ?? []).map((g) => ({
              commissionGroup: { id: g.id, showId: g.showId, name: g.name },
              isManager: true,
          }))
        : ownManagerAssignments;
    const isCommissionGroupManager = managerAssignments.length > 0;
    const groupsLoading = isSuperadmin ? venueGroupsLoading : ownGroupsLoading;

    const activeGroupId = selectedGroupId ?? managerAssignments[0]?.commissionGroup?.id ?? null;

    const { data: bookingsData, isLoading: bookingsLoading } = useGetCommissionGroupBookings(
        activeGroupId,
        { page: bookingsPage, pageSize: bookingsRowsPerPage, sortField, sortDir },
        {
            query: {
                enabled: !!activeGroupId && activeTab === 1,
                placeholderData: (prev) => prev,
                onError: notifyError,
            },
        },
    );

    // Show titles, so a manager of groups on different shows can tell them apart
    // in the group selector. Shared (deduped) with the report tab's shows query.
    const { data: showsData } = useGetShows(
        { pageSize: 1000 },
        { query: { enabled: !!user?.venueId, onError: notifyError } },
    );
    const showTitleById = new Map((showsData?.shows ?? []).map((s) => [s.id, s.title]));
    const groupLabel = (assignment) => {
        const showTitle = showTitleById.get(assignment?.commissionGroup?.showId);
        const name = assignment?.commissionGroup?.name;
        return showTitle ? `${name} - ${showTitle}` : name;
    };

    const handleSortChange = (field) => {
        if (sortField === field) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
        setBookingsPage(0);
    };

    const getPaginatedBookings = () => {
        const items = bookingsData?.items ?? [];
        if (sortField !== 'customers' && sortField !== 'lineTotal') return items;
        const dir = sortDir === 'desc' ? -1 : 1;
        const key = sortField === 'customers' ? 'customerCount' : 'lineTotal';
        return [...items].sort((a, b) => ((a[key] ?? 0) - (b[key] ?? 0)) * dir);
    };
    const paginatedBookings = getPaginatedBookings();

    if (!user || groupsLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isCommissionGroupManager && !isSuperadmin) return null;

    if (!isCommissionGroupManager) {
        return (
            <PageWithTitle
                title={t('commissionGroups.managementTitle') || 'Commission Group Management'}
            >
                <Typography color="text.secondary">
                    {t('commissionGroups.management.noGroups') ||
                        'No commission groups found for this venue.'}
                </Typography>
            </PageWithTitle>
        );
    }

    const activeAssignment = managerAssignments.find((a) => a.commissionGroup.id === activeGroupId);

    return (
        <PageWithTitle
            title={t('commissionGroups.managementTitle') || 'Commission Group Management'}
        >
            <Box sx={{ mb: 2, width: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    {managerAssignments.length > 1 ? (
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: { xs: '100%', sm: 260 },
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            <InputLabel>{t('commissionGroups.management.selectGroup')}</InputLabel>
                            <Select
                                value={activeGroupId ?? ''}
                                label={t('commissionGroups.management.selectGroup')}
                                onChange={(e) => setSelectedGroupId(Number(e.target.value))}
                            >
                                {managerAssignments.map((a) => (
                                    <MenuItem
                                        key={a.commissionGroup.id}
                                        value={a.commissionGroup.id}
                                    >
                                        {groupLabel(a)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        activeAssignment && (
                            <Typography variant="h6" color="text.secondary">
                                {groupLabel(activeAssignment)}
                            </Typography>
                        )
                    )}
                </Box>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ mt: 1 }}
                >
                    <Tab label={t('commissionGroups.management.members')} />
                    <Tab label={t('commissionGroups.management.bookings')} />
                    <Tab label={t('reports.title')} />
                </Tabs>
            </Box>

            {activeTab === 0 && <CommissionGroupMembers commissionGroupId={activeGroupId} />}

            {activeTab === 1 && (
                <>
                    <TableContainer sx={{ maxHeight: '65vh' }}>
                        <Table stickyHeader sx={{ minWidth: 800, '& thead th': { top: 0 } }}>
                            <TableHead>
                                <TableRow>
                                    {BOOKING_COLUMNS.map(({ field, tKey, align }) => (
                                        <TableCell key={field} align={align}>
                                            <TableSortLabel
                                                active={sortField === field}
                                                direction={sortField === field ? sortDir : 'asc'}
                                                onClick={() => handleSortChange(field)}
                                            >
                                                {t(
                                                    `commissionGroups.management.bookingColumns.${tKey}`,
                                                )}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bookingsLoading ? (
                                    <SkeletonTable rows={5} cells={8} />
                                ) : paginatedBookings.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography color="text.secondary">
                                                {t('commissionGroups.management.noBookings')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedBookings.map((item) => (
                                        <TableRow key={item.bookingId}>
                                            <TableCell>{item.agentName}</TableCell>
                                            <TableCell>
                                                {item.bookingId}
                                                <Tooltip title={t('common.edit')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            navigate(
                                                                `/bookings/edit/${item.bookingId}`,
                                                            )
                                                        }
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <div>{item.contact}</div>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {item.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{item.availability}</TableCell>
                                            <TableCell>{item.customers}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={item.bookingStatus}
                                                    size="small"
                                                    color={
                                                        STATUS_COLOR[item.bookingStatus] ??
                                                        'warning'
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatCentsToUsd(item.lineTotal)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatCentsToUsd(item.commissionAmount)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                                {(bookingsData?.total ?? 0) > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} />
                                        <TableCell align="right">
                                            <strong>
                                                {formatCentsToUsd(bookingsData.totalLineAmount)}
                                            </strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>
                                                {formatCentsToUsd(bookingsData.totalCommission)}
                                            </strong>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={bookingsData?.total ?? 0}
                        page={bookingsPage}
                        rowsPerPage={bookingsRowsPerPage}
                        rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                        onPageChange={(_, newPage) => setBookingsPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setBookingsRowsPerPage(parseInt(e.target.value, 10));
                            setBookingsPage(0);
                        }}
                        labelRowsPerPage={t('labelRowsPerPage')}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                    />
                </>
            )}

            {activeTab === 2 && (
                <AgentPerformanceTab venueId={user?.venueId} commissionGroupId={activeGroupId} />
            )}
        </PageWithTitle>
    );
};

export default CommissionGroupManagement;
